import { ArrowLeft } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { SaveStatus } from "@/components/SaveStatus";
import { toast } from "@/components/ui/sonner";
import type { ApiError } from "@/lib/api/unwrap";
import { useAutosave } from "@/lib/hooks/useAutosave";
import { cn } from "@/lib/utils/cn";

import {
  patchProfile,
  qKeys,
  useCharacteristics,
  useCompanySizes,
  useProfile,
  useProgress,
  useSectors,
} from "../api";
import { ProfileFields } from "../components/ProfileFields";
import { RespondentSection } from "../components/RespondentSection";
import { ErrorBlock, LoadingBlock } from "../components/QueryState";
import {
  CANONICAL_ADDRESS_FIELDS,
  PROFILE_FIELD_KEYS,
  REQUIRED_FIELDS,
  splitFieldErrors,
  toPatch,
  toValues,
} from "../profile-utils";
import { EMPTY_PROFILE, type ProfileFormValues } from "../types";

/** Cycling tints for the identity rail's sector chips. */
const CHIP_TONE = [
  "bg-theme-economic-soft text-theme-economic-ink",
  "bg-theme-environment-soft text-theme-environment-ink",
  "bg-theme-social-soft text-theme-social-ink",
  "bg-theme-governance-soft text-theme-governance-ink",
];

/** Circular required-fields completion meter for the identity rail. */
function CompletionRing({ value }: { value: number }) {
  const r = 25;
  const circumference = 2 * Math.PI * r;
  const done = value >= 100;
  return (
    <div className="relative size-[58px] flex-none">
      <svg width="58" height="58" viewBox="0 0 58 58" className="-rotate-90">
        <circle
          cx="29"
          cy="29"
          r={r}
          fill="none"
          strokeWidth="6"
          className="stroke-muted"
        />
        <circle
          cx="29"
          cy="29"
          r={r}
          fill="none"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - value / 100)}
          className={cn(
            "transition-[stroke-dashoffset] duration-500",
            done ? "stroke-success" : "stroke-primary",
          )}
        />
      </svg>
      <span className="absolute inset-0 grid place-items-center text-sm font-bold tabular-nums">
        {value}%
      </span>
    </div>
  );
}

/**
 * The enterprise profile as a side-door (not a wizard step). Reachable from the
 * diagnostic header; edits autosave. A sticky identity rail gives the page a
 * sense of *whose* profile this is (name, type, sectors) plus live completion
 * and save state. Once the diagnostic has started, sectors & characteristics
 * lock and render as a read-only chip summary (see ProfileFields).
 */
export function ProfileEditor({
  profileId,
  onBack,
  backLabel,
}: {
  profileId: string;
  onBack: () => void;
  /** Back-link label. Defaults to the in-questionnaire "back to questionnaire"
   *  wording; the standalone /enterprise-profile page overrides it. */
  backLabel?: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const profile = useProfile(profileId);
  const sectors = useSectors();
  const characteristics = useCharacteristics();
  const companySizes = useCompanySizes();
  const progress = useProgress();
  const locked = (progress.data?.answered_actions ?? 0) > 0;

  // Server-side field errors (e.g. a name whose showcase slug is already
  // taken). The API answers 400 with the message under the offending field, so
  // show it there — the way the Django identification form does — instead of
  // collapsing every failure into one toast that names no field.
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof ProfileFormValues, string>>
  >({});

  // What the server made of an address we sent, kept outside React state so a
  // debounce that was already scheduled can still see it.
  const canonical = useRef<
    Partial<Record<(typeof CANONICAL_ADDRESS_FIELDS)[number], { from: string; to: string }>>
  >({});

  const autosave = useAutosave<ProfileFormValues>({
    save: async (data, signal) => {
      // A save scheduled before the previous one answered closed over the
      // address as it was typed, and component state cannot reach it. Apply
      // what we already know the server made of that exact text, so the
      // payload carries the stored spelling and the API's "address unchanged"
      // guard matches instead of buying another lookup.
      const payload = { ...data };
      for (const field of CANONICAL_ADDRESS_FIELDS) {
        const known = canonical.current[field];
        if (known && payload[field] === known.from) {
          payload[field] = known.to;
        }
      }

      const saved = await patchProfile(profileId, toPatch(payload), signal);

      // What the server stores is the address Google confirmed, not the one
      // that was typed. Remember the mapping for the saves already in flight,
      // and adopt it into the form -- but only for fields the member has not
      // retyped since this save began, so an edit is never overwritten by its
      // own reply.
      for (const field of CANONICAL_ADDRESS_FIELDS) {
        const stored = saved[field] ?? "";
        if (stored === payload[field]) {
          delete canonical.current[field];
        } else {
          canonical.current[field] = { from: payload[field], to: stored };
        }
      }
      setValues((prev) => {
        if (!prev) return prev;
        let next = prev;
        for (const field of CANONICAL_ADDRESS_FIELDS) {
          const stored = saved[field] ?? "";
          if (prev[field] === data[field] && stored !== prev[field]) {
            next = { ...next, [field]: stored };
          }
        }
        return next;
      });
      // The territory is resolved from the address by the save itself, and the
      // identity rail reads it from this cache rather than from the form -- the
      // three are not editable, so they are not form values. Without this the
      // rail would keep the address's *previous* MRC and region until something
      // else happened to refetch, while the Django form, which re-queries the
      // stored profile on every render, shows them at once.
      //
      // setQueryData rather than invalidateQueries: `saved` is the PATCH
      // response and already carries them, so there is nothing to go and ask
      // for. Safe against a superseded save, too -- useAutosave aborts the
      // previous request when a new one starts, so an overtaken save rejects at
      // the await above and never reaches this line.
      qc.setQueryData(qKeys.profile(profileId), saved);
      return saved;
    },
    onSaved: () => setFieldErrors({}),
    onError: (err) => {
      const apiError = err as ApiError;
      const { rendered, unrendered } = splitFieldErrors(
        apiError?.fieldErrors,
        PROFILE_FIELD_KEYS,
      );
      setFieldErrors(rendered as Partial<Record<keyof ProfileFormValues, string>>);
      // A field error already says what is wrong and where, so it needs no
      // toast. Anything this form cannot show — non_field_errors, a field it
      // does not render — still has to be said, or the save fails in silence.
      if (unrendered.length > 0) {
        toast.error(unrendered.join(" "));
      } else if (Object.keys(rendered).length === 0) {
        toast.error(apiError?.detail ?? t("questionnaire.saveError"));
      }
    },
  });
  const { flush } = autosave;

  const initial = useMemo(
    () => (profile.data ? toValues(profile.data) : null),
    [profile.data],
  );
  const [values, setValues] = useState<ProfileFormValues | null>(null);
  const current = values ?? initial;

  const patch = (p: Partial<ProfileFormValues>) => {
    const base = current ?? EMPTY_PROFILE;
    const next = { ...base, ...p };
    setValues(next);
    if (locked) {
      // Never send sector/characteristic edits once locked.
      autosave.save({
        ...next,
        sector_ids: initial?.sector_ids ?? next.sector_ids,
        characteristic_ids: initial?.characteristic_ids ?? next.characteristic_ids,
      });
    } else {
      autosave.save(next);
    }
  };

  // Flush pending edits before leaving the side-door.
  useEffect(() => () => void flush(), [flush]);

  const handleBack = async () => {
    await flush();
    onBack();
  };

  if (
    profile.isLoading ||
    sectors.isLoading ||
    characteristics.isLoading ||
    companySizes.isLoading
  ) {
    return <LoadingBlock />;
  }
  if (profile.error) return <ErrorBlock error={profile.error} />;
  if (!current) return <ErrorBlock error={null} />;

  // Required-fields completion, used by the rail's ring.
  const filled = REQUIRED_FIELDS.filter((f) =>
    String(current[f] ?? "").trim(),
  ).length;
  const pct = Math.round((filled / REQUIRED_FIELDS.length) * 100);
  const remaining = REQUIRED_FIELDS.length - filled;
  const complete = remaining === 0;

  const typeLabel = current.enterprise_type
    ? t(`questionnaire.enterpriseTypes.${current.enterprise_type}`)
    : null;
  // The territory the address resolved to. Read from the saved profile, never
  // from `current`: the three are not form values — they are not editable here,
  // nor on the Django identification form — and what has not been resolved yet
  // is simply not shown, with no label and no placeholder.
  const resolvedProvince = profile.data?.province ?? "";
  const resolvedMrc = profile.data?.mrc ?? "";
  const resolvedRegion = profile.data?.region ?? "";
  const location = [current.city, resolvedProvince].filter(Boolean).join(", ");
  const territory = [
    { label: t("questionnaire.identification.mrc"), value: resolvedMrc },
    { label: t("questionnaire.identification.region"), value: resolvedRegion },
  ].filter((entry) => entry.value);
  const sectorNames = (sectors.data ?? [])
    .filter((s) => current.sector_ids.includes(s.id))
    .map((s) => s.name);

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => void handleBack()}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden />
        {backLabel ?? t("questionnaire.nav.back")}
      </button>

      <div className="grid items-start gap-6 lg:grid-cols-[300px_1fr]">
        {/* ---- Identity rail ---- */}
        <aside className="lg:sticky lg:top-6">
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-md">
            <div className="h-1 bg-gradient-to-r from-theme-governance via-theme-economic to-theme-environment" />
            <div className="p-5">
              <h1 className="font-serif text-2xl font-semibold leading-tight tracking-tight [text-wrap:balance]">
                {current.name || t("questionnaire.identification.editTitle")}
              </h1>

              {(typeLabel || location) && (
                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm text-muted-foreground">
                  {typeLabel && (
                    <span className="inline-flex items-center rounded-full bg-theme-governance-soft px-2.5 py-0.5 text-xs font-semibold text-theme-governance-ink">
                      {typeLabel}
                    </span>
                  )}
                  {typeLabel && location && (
                    <span
                      aria-hidden
                      className="size-1 rounded-full bg-border"
                    />
                  )}
                  {location && <span>{location}</span>}
                </div>
              )}

              {territory.length > 0 && (
                <dl className="mt-2 space-y-0.5 text-sm">
                  {territory.map((entry) => (
                    <div key={entry.label} className="flex gap-1.5">
                      <dt className="text-muted-foreground">{entry.label}</dt>
                      <dd className="min-w-0 font-medium">{entry.value}</dd>
                    </div>
                  ))}
                </dl>
              )}

              {sectorNames.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {sectorNames.map((name, i) => (
                    <span
                      key={name}
                      className={cn(
                        "rounded-full px-2.5 py-0.5 text-xs font-medium",
                        CHIP_TONE[i % CHIP_TONE.length],
                      )}
                    >
                      {name}
                    </span>
                  ))}
                </div>
              )}

              <div className="my-4 h-px bg-border" />

              <div className="flex items-center gap-3.5">
                <CompletionRing value={pct} />
                <div className="min-w-0">
                  <p className="text-sm font-bold">
                    {complete
                      ? t("questionnaire.identification.profileComplete")
                      : t("questionnaire.identification.profileIncomplete")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {complete
                      ? t("questionnaire.identification.allRequiredFilled")
                      : t("questionnaire.identification.fieldsRemaining", {
                          count: remaining,
                        })}
                  </p>
                </div>
              </div>

              <div className="mt-4">
                {autosave.status === "idle" ? (
                  <p className="rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
                    {t("questionnaire.identification.autosaveOn")}
                  </p>
                ) : (
                  <SaveStatus
                    status={autosave.status}
                    className="rounded-lg bg-muted px-3 py-2"
                  />
                )}
              </div>
            </div>
          </div>

          <p className="mt-3 px-1 text-xs leading-relaxed text-muted-foreground">
            {t("questionnaire.identification.identityRailNote")}
          </p>
        </aside>

        {/* ---- Editable sections ---- */}
        <div className="space-y-4">
          <ProfileFields
            values={current}
            onChange={patch}
            sectors={sectors.data ?? []}
            characteristics={characteristics.data ?? []}
            companySizes={companySizes.data ?? []}
            errors={fieldErrors}
            locked={locked}
          />
          <RespondentSection
            profileId={profileId}
            initial={
              profile.data
                ? {
                    first_name: profile.data.respondent_first_name ?? "",
                    last_name: profile.data.respondent_last_name ?? "",
                    position: profile.data.respondent_position ?? "",
                  }
                : undefined
            }
          />
        </div>
      </div>
    </div>
  );
}
